import { EventBusMonitor } from './EventBusMonitor';
import Vue from 'vue';
import { useEmitter } from '../../../pmd/composition/useEmitter';

interface EventBusConfig {
  enabled: boolean;
  listenedForComponents?: string[];
  enableEmitHook?: boolean;
}

export class EventBusWrapper {
  private static instance: EventBusWrapper | undefined;
  private monitor: EventBusMonitor;
  private config: EventBusConfig;

  // Store original methods
  private originalOn = Vue.prototype.$on;
  private originalOff = Vue.prototype.$off;
  private originalEmit = Vue.prototype.$emit;
  private emitter: any;
  private originalEmitterOn: Function | null = null;
  private originalEmitterOff: Function | null = null;

  private constructor(monitor: EventBusMonitor, config: EventBusConfig) {
    this.monitor = monitor;
    this.config = config;
    this.emitter = useEmitter();

    // Store original emitter methods if they exist
    if (this.emitter) {
      this.originalEmitterOn = this.emitter.on;
      this.originalEmitterOff = this.emitter.off;
    }
  }

  static getInstance(monitor: EventBusMonitor, config: EventBusConfig): EventBusWrapper {
    if (!EventBusWrapper.instance) {
      EventBusWrapper.instance = new EventBusWrapper(monitor, config);
    }
    return EventBusWrapper.instance;
  }

  initialize(): void {
    try {
      this.overrideEventBusMethods();
    } catch (e) {
      console.error('Failed to initialize EventBusWrapper:', e);
    }
  }

  private overrideEventBusMethods(): void {
    if (!this.config.enabled) return;

    const monitor = this.monitor;
    const config = this.config;

    // Override $on
    const originalOn = Vue.prototype.$on;
    Vue.prototype.$on = function(eventName: string, callback: Function) {
      try {
        const componentName = this.$options?.name || 'unknown';
        if (!config.listenedForComponents || config.listenedForComponents.includes(componentName)) {
          monitor.trackListener({
            eventName,
            componentName,
            timestamp: Date.now()
          }, true);
        }
      } catch (error) {
        console.warn('EventBusWrapper: Error in $on tracking:', error);
      }

      return originalOn.call(this, eventName, callback);
    };

    // Override emitter.on if emitter exists
    if (this.emitter && this.originalEmitterOn) {
      const origEmitterOn = this.originalEmitterOn;
      const emitterContext = this.emitter;

      this.emitter.on = function(eventName: string, callback?: Function) {
        try {
          // Use a fixed component name for global event bus
          const componentName = 'global-event-bus-instance';
          monitor.trackListener({
            eventName,
            componentName,
            timestamp: Date.now()
          }, true);
        } catch (error) {
          console.warn('EventBusWrapper: Error in emitter.on tracking:', error);
        }

        // Preserve the original context when calling the original method
        return origEmitterOn.apply(emitterContext, [eventName, callback]);
      };
    }

    // Override $off
    const originalOff = Vue.prototype.$off;
    Vue.prototype.$off = function(eventName?: string, callback?: Function) {
      try {
        const componentName = this.$options?.name || 'unknown';
        if (!config.listenedForComponents || config.listenedForComponents.includes(componentName)) {
          // In all cases where we're removing more than just a single callback,
          // set removeAll flag and let the plugin handle the details based on eventName
          if ((eventName === undefined || callback === undefined)) {
            monitor.trackListener({
              eventName: eventName || 'all-events',
              componentName,
              timestamp: Date.now(),
              removeAll: eventName === undefined,
              removeEvent: callback === undefined
            }, false);
          } else {
            // Removing a specific callback for an event
            monitor.trackListener({
              eventName,
              componentName,
              timestamp: Date.now()
            }, false);
          }
        }
      } catch (error) {
        console.warn('EventBusWrapper: Error in $off tracking:', error);
      }

      return originalOff.call(this, eventName, callback);
    };

    // Override emitter.off if emitter exists
    if (this.emitter && this.originalEmitterOff) {
      const origEmitterOff = this.originalEmitterOff;
      const emitterContext = this.emitter;

      this.emitter.off = function(eventName?: string, callback?: Function) {
        try {
          // Use a fixed component name for global event bus
          const componentName = 'global-event-bus-instance';

          // In all cases where we're removing more than just a single callback,
          // set removeAll flag and let the plugin handle the details based on eventName
          if ((eventName === undefined || callback === undefined)) {
            monitor.trackListener({
              eventName: eventName || 'all-events',
              componentName,
              timestamp: Date.now(),
              removeAll: eventName === undefined,
              removeEvent: callback === undefined
            }, false);
          } else {
            // Removing a specific callback for an event
            monitor.trackListener({
              eventName,
              componentName,
              timestamp: Date.now()
            }, false);
          }
        } catch (error) {
          console.warn('EventBusWrapper: Error in emitter.off tracking:', error);
        }

        // Preserve the original context when calling the original method
        return origEmitterOff.apply(emitterContext, [eventName, callback]);
      };
    }

    // Override $emit only if enableEmitHook is true
    if (config.enableEmitHook) {
      const originalEmit = Vue.prototype.$emit;
      Vue.prototype.$emit = function(eventName: string, ...args: any[]) {
        try {
          const componentName = this.$options?.name || 'unknown';
          if (!config.listenedForComponents || config.listenedForComponents.includes(componentName)) {
            monitor.trackEmit({
              eventName,
              args,
              timestamp: Date.now(),
              componentName
            });
          }
        } catch (error) {
          console.warn('EventBusWrapper: Error in $emit tracking:', error);
        }

        return originalEmit.call(this, eventName, ...args);
      };
    }
  }

  cleanup(): void {
    try {
      // Restore original methods
      if (this.config.enabled) {
        Vue.prototype.$on = this.originalOn;
        Vue.prototype.$off = this.originalOff;
        if (this.config.enableEmitHook) {
          Vue.prototype.$emit = this.originalEmit;
        }

        // Restore original emitter methods if they exist
        if (this.emitter) {
          if (this.originalEmitterOn) {
            this.emitter.on = this.originalEmitterOn;
          }
          if (this.originalEmitterOff) {
            this.emitter.off = this.originalEmitterOff;
          }
        }
      }

      // Clear the singleton instance
      EventBusWrapper.instance = undefined;
    } catch (error) {
      console.warn('EventBusWrapper: Error during cleanup:', error);
      // Still attempt to clear the instance even if method restoration fails
      EventBusWrapper.instance = undefined;
    }
  }
}
